// App — router + auth gate + offline banner + upload manager lifecycle.
// Hash routing (brak react-router). Auth gate: gdy brak tokenu → LoginScreen.
//
// Routing v3 (2026-04-17): ROOT "/" = TaskListScreen (bieżący tydzień).
// WeekPickerScreen przeniesiony na "/weeks" (dostępny przyciskiem "Inne tygodnie").

import { Suspense, lazy, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import { useHashRouter, matchPath } from '../hooks/useHashRouter.js';
import { uploadManager } from '../services/uploadManager.js';
import { COLORS } from '../shared/tokens.js';
import OfflineBanner from './ui/OfflineBanner.jsx';
// UZUPEŁNIENIE 2 (12.09.2026): stopka stanu kolejki na dole KAŻDEGO ekranu po zalogowaniu.
import QueueFooter from './ui/QueueFooter.jsx';
// B-1b (13.09.2026, F3): baner „Nowa wersja gotowa" / „Pobieram" / blokada poniżej wymaganej
// wersji — na KAŻDEJ gałęzi (także ekran logowania), tuż pod banerem sieci.
import VersionBanner from './ui/VersionBanner.jsx';
// MONIT 2026-10-02_0526: język UI — subskrypcja u korzenia = re-render całego drzewa po przełączeniu.
import { useLang } from '../hooks/useLang.js';

const LoginScreen            = lazy(() => import('./LoginScreen.jsx'));
const WeekPickerScreen       = lazy(() => import('./WeekPickerScreen.jsx'));
const TaskListScreen         = lazy(() => import('./TaskListScreen.jsx'));
const AkTaskListScreen       = lazy(() => import('./ak/AkTaskListScreen.jsx')); // AK E1: lista terminów Auskundung
const AkEntryPhotosScreen    = lazy(() => import('./ak/AkEntryPhotosScreen.jsx')); // AK E3: zdjęcia wejść (AK-3)
const AdminTaskListScreen    = lazy(() => import('./AdminTaskListScreen.jsx'));
const AddressDetailScreen    = lazy(() => import('./AddressDetailScreen.jsx'));
const InstallTypeScreen      = lazy(() => import('./wizard/InstallTypeScreen.jsx'));
const BuildingConfigScreen   = lazy(() => import('./wizard/BuildingConfigScreen.jsx'));
const PhotoTaskListScreen    = lazy(() => import('./wizard/PhotoTaskListScreen.jsx'));
const FloorWEListScreen      = lazy(() => import('./FloorWEListScreen.jsx'));
const FloorSubfolderScreen   = lazy(() => import('./FloorSubfolderScreen.jsx'));
const KellerViewScreen       = lazy(() => import('./KellerViewScreen.jsx'));
const GVSlotsScreen          = lazy(() => import('./GVSlotsScreen.jsx'));
const SPSlotsScreen          = lazy(() => import('./SPSlotsScreen.jsx'));
const AdminQueueScreen       = lazy(() => import('./AdminQueueScreen.jsx')); // A5
const InfoScreen             = lazy(() => import('./InfoScreen.jsx'));        // ETAP2
const SupportScreen          = lazy(() => import('./SupportScreen.jsx'));     // KROK 1: tryb serwisowy
// Plan posesji B2 (MONIT 2026-10-03_1740 Z1, Z2): edytor planu + pas „Jesteś tutaj" nad podwidokami sekcji.
const PlanEditorScreen       = lazy(() => import('./plan/PlanEditorScreen.jsx'));
// v82 (MONIT 2026-10-04_1357 Zasada 2): plan spawów w APL i „Jak opisać kasetę".
const SplicePlanRoute        = lazy(() => import('./plan/SplicePlanRoute.jsx'));
const SpliceHelpRoute        = lazy(() => import('./plan/SplicePlanRoute.jsx').then((m) => ({ default: m.SpliceHelpRoute })));
const SectionHereBar         = lazy(() => import('./plan/SectionHereBar.jsx'));
// N4 (MONIT 2026-10-04_1646): przenumerowanie mieszkań (pełny Admin): stan adresu i podsumowanie operacji.
const RenumberRoute          = lazy(() => import('./renumber/RenumberRoute.jsx'));

function Loading() {
  const { t } = useLang();
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: COLORS.bg, color: COLORS.textMuted,
    }}>
      {t('common.loading')}
    </div>
  );
}

function NotFound({ path }) {
  const { t } = useLang();
  return (
    <div style={{ padding: 24, color: COLORS.textMuted }}>
      <h2>{t('app.notFound')}</h2>
      <p style={{ fontFamily: 'monospace' }}>Path: {path}</p>
      <a href="#/" style={{ color: COLORS.accent }}>{t('app.backHome')}</a>
    </div>
  );
}

function resolveRoute(path, query) {
  let m;

  // ROOT "/" = bieżący tydzień (TaskListScreen bez query)
  if (path === '/' || path === '')  return { Comp: TaskListScreen, params: {}, query };
  // "/tasks?from&to" = wybrany tydzień (TaskListScreen z query) — używane po wyborze z WeekPicker
  if (path === '/tasks')            return { Comp: TaskListScreen, params: {}, query };
  // "/weeks" = WeekPicker (klik "Inne tygodnie" z TaskList)
  if (path === '/weeks')            return { Comp: WeekPickerScreen, params: {} };

  m = matchPath('/address/:id', path);
  if (m) return { Comp: AddressDetailScreen, params: m };

  // AK E3 (MONIT 2026-10-10_2340 pkt 5): ekran „Zdjęcia wejść” (AK-3), kontekst listy AK w query.
  m = matchPath('/address/:id/ak-photos', path);
  if (m) return { Comp: AkEntryPhotosScreen, params: m, query };

  m = matchPath('/address/:id/wizard', path);
  if (m) return { Comp: InstallTypeScreen, params: m };

  m = matchPath('/address/:id/wizard/config', path);
  if (m) return { Comp: BuildingConfigScreen, params: m, query };

  // ETAP2: widok Info (AKP's + Notiz). Przed /address/:id/floor/* (inna liczba segmentów,
  // ale trzymamy blisko tras adresu dla czytelności).
  m = matchPath('/address/:id/info', path);
  if (m) return { Comp: InfoScreen, params: m, query };

  // Plan posesji B2 (D1, D2): edycja planu adresu (czysty rec-ID; zapis pilnuje wyłącznik).
  m = matchPath('/address/:id/plan', path);
  if (m) return { Comp: PlanEditorScreen, params: m, query };

  m = matchPath('/address/:id/splice-plan', path);
  if (m) return { Comp: SplicePlanRoute, params: m, query };
  m = matchPath('/address/:id/splice-plan/help', path);
  if (m) return { Comp: SpliceHelpRoute, params: m, query };

  m = matchPath('/address/:id/renumber', path);
  if (m) return { Comp: RenumberRoute, params: m, query };
  m = matchPath('/address/:id/renumber/op/:opId', path);
  if (m) return { Comp: RenumberRoute, params: m, query };

  m = matchPath('/address/:id/floor/:floor', path);
  if (m) return { Comp: FloorWEListScreen, params: m };

  m = matchPath('/address/:id/floor/:floor/we/:we', path);
  if (m) return { Comp: PhotoTaskListScreen, params: m };

  m = matchPath('/address/:id/floor/:floor/sub/:subfolder', path);
  if (m) return { Comp: FloorSubfolderScreen, params: m };

  m = matchPath('/address/:id/keller', path);
  if (m) return { Comp: KellerViewScreen, params: m };

  m = matchPath('/address/:id/keller/gv/:gv', path);
  if (m) return { Comp: GVSlotsScreen, params: m };

  // REFORMA E2a KOREKTA (sekcja 8, brak piwnicy): grupy piwnicy (APL1/GV1/SP1/BS)
  // seedowane na EG — ten sam GVSlotsScreen z parametrem piętra.
  m = matchPath('/address/:id/floor/:floor/kg/:gv', path);
  if (m) return { Comp: GVSlotsScreen, params: m };

  m = matchPath('/address/:id/keller/sp/:sp', path);
  if (m) return { Comp: SPSlotsScreen, params: m };

  // Krok 2b — SP na piętrze (T4/T5), np. /address/:id/floor/OG.1/sp/sp-og-1
  m = matchPath('/address/:id/floor/:floor/sp/:sp', path);
  if (m) return { Comp: SPSlotsScreen, params: m };

  // Krok 2b — BS zbiorczy dla T5 (floor_key='BS').
  // B1 (2026-06-12): 3 nazwane sloty (auto-akcept) przez FloorSubfolderScreen
  // (subfolder='bs_zbiorczy' → group_key='bs_zbiorczy'). Wcześniej PhotoTaskListScreen
  // renderował 6 slotów "jak WE" (getRequiredSlotsForWE) bez odpowiednika w photo_slots.
  m = matchPath('/address/:id/bs', path);
  if (m) return { Comp: FloorSubfolderScreen, params: { ...m, floor: 'BS', subfolder: 'bs_zbiorczy' } };

  // A5 (2026-06-12): ukryty ekran admina — diagnostyka kolejki offline (read-only).
  // Gate isAdmin WEWNĄTRZ komponentu (resolveRoute jest rolowo-ślepy).
  if (path === '/admin/queue') return { Comp: AdminQueueScreen, params: {}, query };

  // PAKIET 6 fixpack (30.08): wejście ADMINA na ekran Serwis (sekcja "Ustawienia
  // systemowe"). Role serwisowe tu nie docierają (early return wyżej renderuje im
  // SupportScreen bez routingu). Gate ról WEWNĄTRZ komponentu bez zmian
  // (fail-closed: technik dostaje "Brak dostępu", backend i tak wymusza 403).
  if (path === '/serwis') return { Comp: SupportScreen, params: {}, query };

  return null;
}

export default function App() {
  const { isLoggedIn, isAdmin, role, isServiceRole, isAk } = useAuth();
  const router = useHashRouter();
  // Język: zmiana = re-render od korzenia (ekrany bez React.memo przerysowują teksty od razu).
  useLang();

  // GATE'Y SUPPORT (monit 1215 zad.3): role serwisowe NIE startują toru
  // wysyłkowego (bramka backendu odbija 403, a serie 403 nadpisywałyby kiedyś
  // diagnostyczne lastError). start({sendEnabled:false}) robi wyłącznie porządki
  // lokalne: reset zombie + skan martwych blobów → rejestr „Utracone" (R2b-b,
  // działa w każdej roli — decyzja Martina 19.08). Deps [isServiceRole]:
  // przelogowanie support→team na tej samej stronie uruchamia pełny tor.
  useEffect(() => {
    if (isServiceRole) {
      uploadManager.start({ sendEnabled: false });
      return undefined;
    }
    uploadManager.start();
    return () => uploadManager.stop();
  }, [isServiceRole]);

  if (!isLoggedIn) {
    return (
      <Suspense fallback={<Loading />}>
        <OfflineBanner />
        <VersionBanner />
        <LoginScreen />
      </Suspense>
    );
  }

  // KROK 1 (18.08): role serwisowe lądują PROSTO na ekranie Serwis — żadnego
  // routingu po aplikacji (backend i tak blokuje endpointy biznesowe 403,
  // bramka w plugins/auth.js). Cała apka dla tej roli = jeden ekran.
  if (role === 'support' || role === 'support-plus') {
    return (
      <Suspense fallback={<Loading />}>
        <OfflineBanner />
        <VersionBanner />
        <SupportScreen router={router} />
        <QueueFooter />
      </Suspense>
    );
  }

  // K19 (Martin): admin po zalogowaniu wchodzi na "/" do AdminTaskListScreen.
  // B3.5.3 Q2 (Martin): dodany dedicated route /admin/tasks dla URL persistence
  // (zamiast `?from=...&to=...` na "/") — czytelniejsze + nie kollajduje z technikowym "/".
  // Backward compat: "/" dla admina nadal działa (np. login redirect) → wyświetla AdminTaskListScreen.
  if (isAdmin && (router.path === '/' || router.path === '' || router.path === '/admin/tasks')) {
    return (
      <Suspense fallback={<Loading />}>
        <OfflineBanner />
        <VersionBanner />
        <AdminTaskListScreen router={router} query={router.query} />
        <QueueFooter />
      </Suspense>
    );
  }

  // AK E1 (MONIT 2026-10-10_1515, W10): technik z PIN-em z tabeli AK Teamy widzi od razu swoje terminy
  // Auskundung, bez przełącznika. "/" i "/tasks" (powrót z adresu) = lista AK; reszta tras jak dotąd.
  if (isAk && (router.path === '/' || router.path === '' || router.path === '/tasks')) {
    return (
      <Suspense fallback={<Loading />}>
        <OfflineBanner />
        <VersionBanner />
        <AkTaskListScreen router={router} query={router.query} />
        <QueueFooter />
      </Suspense>
    );
  }

  const route = resolveRoute(router.path, router.query);
  if (!route) {
    return <NotFound path={router.path} />;
  }

  const { Comp, params = {}, query = {} } = route;
  // Plan posesji B2 (D5): pas „Jesteś tutaj" na każdym podwidoku sekcji ≠ 0 (`:id = recXXX~n`).
  const sectionRoute = typeof params.id === 'string' && /~([1-9]\d?)$/.test(params.id);

  return (
    <Suspense fallback={<Loading />}>
      <OfflineBanner />
      <VersionBanner />
      {sectionRoute && <SectionHereBar addressRef={params.id} />}
      <Comp params={params} query={query} router={router} />
      <QueueFooter />
    </Suspense>
  );
}
